import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { auth, db } from "../firebase/config";
import type {
  CreateMascotBaseVersionRequest,
  CreateMascotStatePlanRequest,
  GenerateMascotBaseImageRequest,
  GenerateMascotStateImagesRequest,
  MascotApiResponse,
  MascotBaseVersion,
  MascotCharacterProfile,
  MascotStateJob,
} from "../types/mascot";

const BASE_COLLECTION = "mascotBaseVersions";
const STATE_COLLECTION = "mascotStateJobs";

const CREATE_BASE_ENDPOINT =
  process.env.REACT_APP_CREATE_MASCOT_BASE_VERSION_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/createMascotBaseVersion";
const GENERATE_BASE_ENDPOINT =
  process.env.REACT_APP_GENERATE_MASCOT_BASE_IMAGE_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/generateMascotBaseImage";
const CREATE_STATE_PLAN_ENDPOINT =
  process.env.REACT_APP_CREATE_MASCOT_STATE_PLAN_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/createMascotStatePlan";
const GENERATE_STATE_IMAGES_ENDPOINT =
  process.env.REACT_APP_GENERATE_MASCOT_STATE_IMAGES_URL ||
  "https://europe-west1-translatorx-5774e.cloudfunctions.net/generateMascotStateImages";

class MascotService {
  private toIso(value: any): string {
    if (value instanceof Timestamp) return value.toDate().toISOString();
    if (value?.toDate && typeof value.toDate === "function") {
      return value.toDate().toISOString();
    }
    const parsed = new Date(value || Date.now());
    return Number.isNaN(parsed.getTime())
      ? new Date().toISOString()
      : parsed.toISOString();
  }

  private normalizeProfile(input: any): MascotCharacterProfile {
    return {
      name: String(input?.name || "Nova"),
      archetype: String(input?.archetype || "Friendly helper"),
      speciesOrForm: String(input?.speciesOrForm || "Stylized creature"),
      silhouette: String(input?.silhouette || "Rounded silhouette"),
      personality: String(input?.personality || "Playful"),
      colorPalette: String(input?.colorPalette || "Coral and teal"),
      wardrobeOrAccessories: String(input?.wardrobeOrAccessories || "Minimal badge"),
    };
  }

  private async post<T>(url: string, body: Record<string, unknown>): Promise<T> {
    await auth.authStateReady();
    const user = auth.currentUser;
    if (!user) throw new Error("Authentication required.");
    const idToken = await user.getIdToken();
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });
    const text = await response.text();
    let data: any = null;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = null;
    }
    if (!response.ok) {
      throw new Error(
        data?.error || data?.message || text || `Request failed (${response.status})`,
      );
    }
    return data as T;
  }

  private parseBaseVersion(id: string, data: Record<string, any>): MascotBaseVersion {
    return {
      versionId: String(data.versionId || id),
      appId: String(data.appId || ""),
      appName: String(data.appName || ""),
      requesterUserId: String(data.requesterUserId || ""),
      versionNumber: Number(data.versionNumber || 1),
      status:
        data.status === "review_pending" ||
        data.status === "generating" ||
        data.status === "completed" ||
        data.status === "failed"
          ? data.status
          : "review_pending",
      inputs: {
        creativeDirection: String(data.inputs?.creativeDirection || ""),
        includeAppContext: data.inputs?.includeAppContext !== false,
        aiMode: data.inputs?.aiMode !== false,
      },
      characterProfile: this.normalizeProfile(data.characterProfile),
      baseImagePrompt: String(data.baseImagePrompt || ""),
      baseAsset:
        data.baseAsset && typeof data.baseAsset === "object" ? data.baseAsset : null,
      model: data.model ? String(data.model) : "",
      createdAt: this.toIso(data.createdAt),
      updatedAt: this.toIso(data.updatedAt),
    };
  }

  private parseStateJob(id: string, data: Record<string, any>): MascotStateJob {
    return {
      jobId: String(data.jobId || id),
      appId: String(data.appId || ""),
      appName: String(data.appName || ""),
      requesterUserId: String(data.requesterUserId || ""),
      versionId: String(data.versionId || ""),
      status:
        data.status === "review_pending" ||
        data.status === "generating" ||
        data.status === "completed" ||
        data.status === "failed"
          ? data.status
          : "review_pending",
      model: data.model ? String(data.model) : "",
      inputs: {
        states: Array.isArray(data.inputs?.states)
          ? data.inputs.states.map((item: any) => String(item || "")).filter(Boolean)
          : [],
        creativeDirection: String(data.inputs?.creativeDirection || ""),
      },
      characterProfile: this.normalizeProfile(data.characterProfile),
      baseImagePrompt: String(data.baseImagePrompt || ""),
      statePrompts: Array.isArray(data.statePrompts)
        ? data.statePrompts.map((item: any) => ({
            state: String(item?.state || ""),
            prompt: String(item?.prompt || ""),
          }))
        : [],
      stateAssets: Array.isArray(data.stateAssets) ? data.stateAssets : [],
      zipAsset:
        data.zipAsset && typeof data.zipAsset === "object" ? data.zipAsset : null,
      createdAt: this.toIso(data.createdAt),
      updatedAt: this.toIso(data.updatedAt),
    };
  }

  async createBaseVersion(
    payload: CreateMascotBaseVersionRequest,
  ): Promise<MascotApiResponse> {
    return this.post<MascotApiResponse>(
      CREATE_BASE_ENDPOINT,
      payload as unknown as Record<string, unknown>,
    );
  }

  async generateBaseImage(
    payload: GenerateMascotBaseImageRequest,
  ): Promise<MascotApiResponse> {
    return this.post<MascotApiResponse>(
      GENERATE_BASE_ENDPOINT,
      payload as unknown as Record<string, unknown>,
    );
  }

  async createStatePlan(
    payload: CreateMascotStatePlanRequest,
  ): Promise<MascotApiResponse> {
    return this.post<MascotApiResponse>(
      CREATE_STATE_PLAN_ENDPOINT,
      payload as unknown as Record<string, unknown>,
    );
  }

  async generateStateImages(
    payload: GenerateMascotStateImagesRequest,
  ): Promise<MascotApiResponse> {
    return this.post<MascotApiResponse>(
      GENERATE_STATE_IMAGES_ENDPOINT,
      payload as unknown as Record<string, unknown>,
    );
  }

  async listBaseVersions(params?: { appId?: string }): Promise<MascotBaseVersion[]> {
    await auth.authStateReady();
    const user = auth.currentUser;
    const requesterUserId = user?.email || user?.uid || "";
    if (!requesterUserId) return [];
    const constraints: any[] = [where("requesterUserId", "==", requesterUserId)];
    if (params?.appId) constraints.push(where("appId", "==", params.appId));
    const snapshot = await getDocs(query(collection(db, BASE_COLLECTION), ...constraints));
    return snapshot.docs
      .map((docSnap) => this.parseBaseVersion(docSnap.id, docSnap.data()))
      .sort(
        (left, right) =>
          new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
      );
  }

  async listStateJobs(params?: { appId?: string }): Promise<MascotStateJob[]> {
    await auth.authStateReady();
    const user = auth.currentUser;
    const requesterUserId = user?.email || user?.uid || "";
    if (!requesterUserId) return [];
    const constraints: any[] = [where("requesterUserId", "==", requesterUserId)];
    if (params?.appId) constraints.push(where("appId", "==", params.appId));
    const snapshot = await getDocs(query(collection(db, STATE_COLLECTION), ...constraints));
    return snapshot.docs
      .map((docSnap) => this.parseStateJob(docSnap.id, docSnap.data()))
      .sort(
        (left, right) =>
          new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime(),
      );
  }

  async getBaseVersion(versionId: string): Promise<MascotBaseVersion | null> {
    const snap = await getDoc(doc(db, BASE_COLLECTION, versionId));
    if (!snap.exists()) return null;
    return this.parseBaseVersion(snap.id, snap.data());
  }

  async getStateJob(jobId: string): Promise<MascotStateJob | null> {
    const snap = await getDoc(doc(db, STATE_COLLECTION, jobId));
    if (!snap.exists()) return null;
    return this.parseStateJob(snap.id, snap.data());
  }
}

const mascotService = new MascotService();
export default mascotService;
